import React, { useState, useEffect } from 'react'
import { X, UserPlus, AlertCircle, AlertTriangle, Check } from 'lucide-react'
import { teamService } from './teamService'

interface AddAthleteModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (message: string) => void
  organizationId: string
  teamId: string
  seasonId: string
}

const POSITIONS = ['QB', 'C', 'WR', 'SLOT', 'RUSH', 'LB', 'CB']

export const AddAthleteModal: React.FC<AddAthleteModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  organizationId,
  teamId,
  seasonId,
}) => {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [primaryPosition, setPrimaryPosition] = useState('WR')
  const [secondaryPositions, setSecondaryPositions] = useState<string[]>([])
  const [unit, setUnit] = useState<'offense' | 'defense' | 'both'>('offense')
  const [jerseyNumber, setJerseyNumber] = useState<string>('')
  const [trackingStartedAt, setTrackingStartedAt] = useState<string>(
    new Date().toISOString().split('T')[0]
  )
  const [inviteNow, setInviteNow] = useState(false)
  const [isHomonymWarning, setIsHomonymWarning] = useState(false)
  const [confirmHomonym, setConfirmHomonym] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) {
      setFirstName('')
      setLastName('')
      setEmail('')
      setPrimaryPosition('WR')
      setSecondaryPositions([])
      setUnit('offense')
      setJerseyNumber('')
      setTrackingStartedAt(new Date().toISOString().split('T')[0])
      setInviteNow(false)
      setIsHomonymWarning(false)
      setConfirmHomonym(false)
      setErrorMsg(null)
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = prevOverflow
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleToggleSecondaryPosition = (pos: string) => {
    if (pos === primaryPosition) return
    if (secondaryPositions.includes(pos)) {
      setSecondaryPositions(secondaryPositions.filter((p) => p !== pos))
    } else {
      setSecondaryPositions([...secondaryPositions, pos])
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg(null)

    if (!firstName.trim() || !lastName.trim()) {
      setErrorMsg('Nombre y apellidos son campos obligatorios.')
      return
    }

    if (inviteNow && (!email.trim() || !email.includes('@'))) {
      setErrorMsg('Debes indicar un correo electrónico válido para emitir la invitación.')
      return
    }

    setIsSubmitting(true)
    try {
      const res = await teamService.createAthleteAndRoster({
        organizationId,
        teamId,
        seasonId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() ? email.trim().toLowerCase() : undefined,
        primaryPosition,
        secondaryPositions,
        unit,
        jerseyNumber: jerseyNumber ? parseInt(jerseyNumber, 10) : undefined,
        trackingStartedAt,
        invite: inviteNow,
        allowHomonym: confirmHomonym,
      })

      if (res.success) {
        onSuccess(
          res.invitation_token
            ? `Atleta ${firstName} ${lastName} creado e invitado exitosamente.`
            : `Ficha de ${firstName} ${lastName} creada y añadida al seguimiento de la selección.`
        )
        onClose()
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al registrar el atleta.'
      if (msg.includes('HOMONYM_DETECTED')) {
        setIsHomonymWarning(true)
        setErrorMsg(null)
      } else {
        setErrorMsg(msg)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="modal-content"
        style={{ maxWidth: '520px', width: '92vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <UserPlus size={18} style={{ color: 'var(--brand-red)' }} />
            <h2 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>
              Añadir Atleta a la Selección
            </h2>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            aria-label="Cerrar modal"
            style={{ padding: '0.25rem' }}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
            {errorMsg && (
              <div
                style={{
                  padding: '0.75rem',
                  backgroundColor: 'var(--status-danger-bg)',
                  color: 'var(--status-danger-text)',
                  border: '1px solid var(--status-danger-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.82rem',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                }}
              >
                <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>{errorMsg}</div>
              </div>
            )}

            {isHomonymWarning && (
              <div
                style={{
                  padding: '0.85rem',
                  backgroundColor: 'rgba(234, 179, 8, 0.12)',
                  color: 'var(--text-main)',
                  border: '1px solid var(--brand-yellow)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.82rem',
                  marginBottom: '1rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <AlertTriangle size={18} style={{ color: 'var(--brand-yellow)', flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <strong>Coincidencia de nombre detectada (Posible homónimo)</strong>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                      Ya existe un atleta registrado con el nombre <strong>{firstName} {lastName}</strong> en el sistema.
                      Si se trata de otra persona diferente (homónimo legítimo en la federación), marque la casilla a continuación para autorizar la creación de la nueva ficha deportiva independiente.
                    </p>
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, marginTop: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={confirmHomonym}
                    onChange={(e) => setConfirmHomonym(e.target.checked)}
                  />
                  <span>Confirmo que es otra persona diferente (homónimo) y deseo crear su ficha independiente</span>
                </label>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label
                  htmlFor="athlete-first-name"
                  style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}
                >
                  Nombre *
                </label>
                <input
                  id="athlete-first-name"
                  type="text"
                  className="form-control"
                  placeholder="Ej. Martín"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value)
                    setIsHomonymWarning(false)
                    setConfirmHomonym(false)
                  }}
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="athlete-last-name"
                  style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}
                >
                  Apellidos *
                </label>
                <input
                  id="athlete-last-name"
                  type="text"
                  className="form-control"
                  placeholder="Ej. De la Torre"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value)
                    setIsHomonymWarning(false)
                    setConfirmHomonym(false)
                  }}
                  required
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                  Posición Principal *
                </label>
                <select
                  className="form-control"
                  value={primaryPosition}
                  onChange={(e) => {
                    const newPos = e.target.value
                    setPrimaryPosition(newPos)
                    setSecondaryPositions(secondaryPositions.filter((p) => p !== newPos))
                  }}
                >
                  {POSITIONS.map((pos) => (
                    <option key={pos} value={pos}>
                      {pos}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                  Unidad Habitual *
                </label>
                <select
                  className="form-control"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value as 'offense' | 'defense' | 'both')}
                >
                  <option value="offense">Ataque</option>
                  <option value="defense">Defensa</option>
                  <option value="both">Ambas (Polivalente)</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Posiciones Secundarias
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {POSITIONS.filter((p) => p !== primaryPosition).map((pos) => {
                  const isChecked = secondaryPositions.includes(pos)
                  return (
                    <button
                      key={pos}
                      type="button"
                      className={`btn ${isChecked ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ padding: '0.25rem 0.55rem', fontSize: '0.75rem' }}
                      onClick={() => handleToggleSecondaryPosition(pos)}
                    >
                      {pos} {isChecked && <Check size={12} style={{ marginLeft: '4px' }} />}
                    </button>
                  )
                })}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                  Dorsal Opcional
                </label>
                <input
                  type="number"
                  min="0"
                  max="99"
                  className="form-control"
                  placeholder="Ej. 12"
                  value={jerseyNumber}
                  onChange={(e) => setJerseyNumber(e.target.value)}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                  Inicio de Seguimiento
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={trackingStartedAt}
                  onChange={(e) => setTrackingStartedAt(e.target.value)}
                />
              </div>
            </div>

            <div
              style={{
                marginTop: '1rem',
                padding: '0.85rem',
                backgroundColor: 'var(--surface-subtle)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--surface-border)',
              }}
            >
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
                <input
                  type="checkbox"
                  checked={inviteNow}
                  onChange={(e) => setInviteNow(e.target.checked)}
                />
                <span>Invitar al atleta inmediatamente por correo</span>
              </label>

              {inviteNow && (
                <div style={{ marginTop: '0.65rem' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>
                    Correo Electrónico para la invitación *
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="atleta@ejemplo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required={inviteNow}
                  />
                  <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                    Se emitirá un enlace único y expirable (7 días) para que active su acceso y complete su perfil.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '0.75rem 1rem' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting || (isHomonymWarning && !confirmHomonym)}>
              {isSubmitting
                ? 'Guardando...'
                : isHomonymWarning && confirmHomonym
                ? 'Confirmar y Crear Ficha Homónima'
                : 'Crear Ficha y Añadir'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
